// PlanSelector.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Card, CardHeader, CardContent, CardActions, Grid2, Stack,
  Typography, Chip, Button, Divider, Radio, ToggleButton, ToggleButtonGroup,
  List, ListItem, ListItemIcon, ListItemText, useTheme, alpha,
  Paper,
  Tooltip,
  Container,
  Alert,
  AlertTitle
} from "@mui/material";
import { School, Apartment, Business, CheckCircle, Info, Lock, Block, LaunchOutlined, Subscript } from "@mui/icons-material";
import API from "../../api/api";
import { useDispatch } from "react-redux";
import { setSnackError } from "../../Reducers/snackErrors";
import SubscriptionState from "../Alerts/SubscriptionState";

const DEFAULT_PLANS = [
    {
    id: "trial",
    name: "Trial",
    icon: <School color="inherit" />,
    priceMonthly: 99,
    priceYearly: 999,
    blurb: "Getting started",
    features: [
        "Full analytics",
        "Data ingestion",
        "Online assessment"
    ],
    highlight: false,
    price_id: '',
    contact: false,
    },
  {
    id: "pilot",
    name: "School Pilot Plan",
    icon: <School  color="inherit" />,
    priceMonthly: 99,
    priceYearly: 999,
    blurb: "Perfect for a single school getting started.",
    features: [
      "Full data analytics",
      "Full data ingestion endpoints",
      "Data ownership",
    ],
    highlight: false,
    price_id: '',
    contact: false,
  },
  {
    id: "district",
    name: "District Pilot Plan",
    icon: <Apartment color="inherit" />,
    priceMonthly: 399,
    priceYearly: 3990,
    blurb: "For districts piloting across multiple schools.",
    features: [
        "Full data analytics",
        "Full data ingestion endpoints",
        "Data ownership",
        "Priority support"
      ],
    highlight: true, // featured
    price_id: '',
    contact: false,
  },
  {
    id: "enterprise",
    name: "Enterprise District Plan",
    icon: <Business color="inherit" />,
    priceMonthly: 0,
    priceYearly: 0,
    blurb: "Large districts with enterprise needs.",
    features: [
      "Unlimited schools*",
      "Unlimited districts*",
      "Unlimited staff accounts*",
      "Priority support",
      "Higher LLM tokens "
    ],
    highlight: false,
    contact: true,
    price_id: ""
  },
];

export default function PlanSelector({plans = DEFAULT_PLANS, value, onChange, defaultBilling = "monthly", onSelectClick, ctaLabel = "Choose plan", onManageSubsription}) {
  const theme = useTheme();
  const [billing, setBilling] = useState(defaultBilling);
  const [subcriptions, setSubscriptions] = useState([]);
  const [currentPlanId, setCurrentPlanId] = useState(null);
  const [currentPlan, setCurrentPlan] = useState(null);

  const handleBilling = (_, next) => {
    if (next) setBilling(next);
  };

  const priceKey = billing === "monthly" ? "cost_monthly" : "cost_yearly";
  const priceSuffix = billing === "monthly" ? "/mo" : "/yr";

  const selectedId = value;
  const isSelected = (id) => id === selectedId;
  const dispatch = useDispatch();

  const featuredColor = theme.palette.mode === "light"
    ? alpha(theme.palette.primary.main, 0.08)
    : alpha(theme.palette.primary.main, 0.18);

  const borderFor = (plan) =>
    `1px solid ${isSelected(plan.id) ? theme.palette.primary.main : theme.palette.divider}`;


  useEffect(() => {
    getSubscriptions();
  }, [])

  const getSubscriptions = async () => {
    try {
        const request = await API.get('/get_subscriptions');
        const {ent, sub, user} = request.data;
        const grouped = groupBy(ent, sub);
        setSubscriptions(grouped);
        if (!user || user === undefined) {
          setCurrentPlanId(null);
          setCurrentPlan(null);
        }
        if (user && user[0]) {
          setCurrentPlanId(user[0].plan_id);
          setCurrentPlan(user[0]);
        }
    }
    catch(error) {
      console.log(error)
      if (error.isAuthError || error.isForbidden) {
          return;
        } 
        if (error.isForbidden) {
          return;
        }
        if (error.isNetworkError) {
          dispatch(setSnackError({ 
            title: 'Connection Error', 
            body: error.message 
          }));
        } else {
          dispatch(setSnackError({
            title: 'Error found',
            body: error.message || 'Unable to get data.'
          }));
        }
      return;
    }
  }

  const groupBy = (data, ent) => {
    let obj = {}
    data.forEach((item) => {
        const id = item.plan_id;
        const f = ent.find(item => item.id === Number(id));
        if (!obj[id]) {
            obj[id] = {
                id: Number(id),
                name: f.name,
                code: f.code,
                price_id: f.price_id,
                cost_monthly: f.cost_monthly,
                cost_yearly: f.cost_yearly,
                entitlements: [],
            };
        }
        if (obj[id]) {
            obj[id].entitlements.push({
                key: item.key,
                limit_value: item.limit_value,
                enabled: item.enabled,
                enterprise: item.enterprise,
            });
        }
    });
    return Object.values(obj);
  }
  const formatLabel = (key) =>
    key
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <Paper sx={{p: 2}}>
    <Box>
      {/* Billing toggle */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6" fontWeight={'bold'}>Choose a subscription</Typography>
        <ToggleButtonGroup
          value={billing}
          exclusive
          onChange={handleBilling}
          size="small"
          color="primary"
        >
          <ToggleButton value="monthly">Monthly</ToggleButton>
          <ToggleButton value="yearly">Yearly</ToggleButton>
        </ToggleButtonGroup>
      </Stack>


      
      <SubscriptionState sub={currentPlan} onManage={onManageSubsription} />
      

      <Grid2 container spacing={2}>
        {plans && plans.map((plan) => {
            const source = subcriptions.find(item => item.code === plan.id);
            let selected = false
            if (source) { selected = currentPlanId === source.id }
          return (
            <Grid2 key={plan.id} item size={{xs:12, sm: 6, md: 4}}>
              <Card
                role="radio"
                aria-checked={selected}
                tabIndex={0}
                onClick={() => onChange?.(plan.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onChange?.(plan.id);
                  }
                }}
                sx={{
                  cursor: "pointer",
                  border: borderFor(plan),
                  borderRadius: 3,
                  height: "100%",
                  outline: "none",
                  bgcolor: "background.paper",
                  transition: "box-shadow .2s, transform .02s",
                  "&:hover": { boxShadow: 6 },
                  "&[aria-checked='true']": { boxShadow: 8 },
                }}
              >
                <CardHeader
                  avatar={<Box sx={{
                    p: 1, borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                    color: theme.palette.primary.main,
                    display: "Grid2", placeItems: "center"
                  }}>{plan.icon}</Box>}
                  title={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="h6" fontWeight={'bold'}>{plan.name}</Typography>
                      {plan.highlight && <Chip size="small" color="secondary" label="Most popular" />}
                    </Stack>
                  }
                  subheader={<Typography variant="body2" color="text.secondary">{plan.blurb}</Typography>}
                />

                <CardContent sx={{ pt: 0 }}>
                  {/* Price */}
                  <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 1 }}>
                    <Typography variant="h4" fontWeight={800}>
                      {source ? `$${source[priceKey].toLocaleString()}` : "contact sales"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">{priceSuffix}</Typography>
                  </Stack>

                  <Divider sx={{ my: 1 }} />

                  {/* Features */}
                  <Grid2 container spacing={1}>
                        <Grid2 size={{xs: 12, md: 6}}>
                        <List dense disablePadding>
                        {source && source.entitlements.map((f, i) => {
                            const label = formatLabel(f.key);
                            const secondaryText = f.limit_value
                            ? `Limit: ${f.limit_value}`
                            : f.enabled
                            ? "Enabled"
                            : "Disabled";

                            return (
                            <ListItem key={i} disableGutters sx={{ py: 0.5 }}>
                                <ListItemIcon sx={{ minWidth: 28, color: f.enabled ? "success.main" : "text.disabled" }}>
                                {f.enabled ? <CheckCircle fontSize="small" /> : <Block fontSize="small" />}
                                </ListItemIcon>
                                <ListItemText
                                primary={label}
                                secondary={secondaryText}
                                primaryTypographyProps={{ fontWeight: 500 }}
                                />
                                {f.enterprise && (
                                <Tooltip title="Enterprise-only feature">
                                    <Chip
                                    label="Enterprise"
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    icon={<Lock fontSize="small" />}
                                    sx={{ ml: 1 }}
                                    />
                                </Tooltip>
                                )}
                            </ListItem>
                            );
                        })}
                        </List>
                        </Grid2>

                        <Grid2 size={{xs: 12, md: 6}}>
                        <List dense disablePadding>
                            {plan.features.map((f, i) => (
                            <ListItem key={i} disableGutters sx={{ py: 0.5 }}>
                                <ListItemIcon sx={{ minWidth: 28, color: "success.main" }}>
                                <CheckCircle fontSize="small" />
                                </ListItemIcon>
                                <ListItemText
                                primary={f}
                                />
                            </ListItem>
                            ))}
                        </List>
                        </Grid2>
                  </Grid2>

                </CardContent>

                <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
                  <Radio
                    checked={selected}
                    onChange={() => onChange?.(plan.id)}
                    color="secondary"
                    sx={{ mr: 1 }}
                    inputProps={{ "aria-label": plan.name }}
                  />
                  <Button
                    fullWidth
                    variant={selected ? "contained" : "outlined"}
                    disabled={currentPlan && currentPlan.plan_id > 1}
                    color="inherit"
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelectClick?.(source, currentPlan.stripe_customer_id);
                      onChange?.(source.price_id);
                    }}
                  >
                    {ctaLabel}
                  </Button>
                </CardActions>
              </Card>
            </Grid2>
          );
        })}
      </Grid2>

      {/* Small footnote */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
        <Info fontSize="small" color="action" />
        <Typography variant="caption" color="text.secondary">
          Yearly billing includes a discount versus paying monthly.
        </Typography>
      </Stack>
      
    </Box>
    </Paper>
  );
}